import { type RulebookProfile, useTranslatedTag, useTranslation, useUserInfo, useUserSettings } from "@next-platform/client-common-core";
import { useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import useRulebookProfileAttach from "@next-platform/client-common-core/rulebook/profile/useRulebookProfileAttach";
import { CharacterCardVariant, Chip, Icon, IconSize, IconVariants, Image, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { SubCapabilityKey } from "@next-platform/packages-ts-types";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Col, LocalizedLink, Row } from "@/components";
import AvatarWithModal from "@/components/common/AvatarWithModal";
import Trans from "@/components/Trans/Trans";
import { ROUTES } from "@/constants";
import useCloneRulebook from "@/hooks/useCloneRulebook";
import useToast from "@/hooks/useToast";
import downloadRulebookExport from "@/pages/RulebookEditor/utils/downloadRulebookExport";
import { AvatarPreset } from "@/types";
import { getAvatarUrl } from "@/utils";
import getRulebookVariant from "@/utils/getRulebookVariant";

import { isRulebookShareable, openDraftOrImport } from "./openRulebookDraftOrImport";
import RulebookProfileActions, { RulebookProfileModals } from "./RulebookProfileActions";
import RulebookProfileStats, {
  DefinitionPrivatePill,
  DefinitionSharedPill,
  type RulebookProfileStatsValues,
  RulebookStatusChip,
} from "./RulebookProfileStats";

type RulebookProfileHeaderProps = {
  profile: RulebookProfile;
  /** False until the draft store is scoped to this account — Edit/Clone write there. */
  isDraftOwnerReady: boolean;
  /** Called after a successful attach so the page can refresh the attached counts. */
  onRulebookAttached: () => void;
};

/** Owner + non-public/non-active rulebooks carry the same status badge the My Rulebooks card shows. */
const rulebookProfileVariant = (profile: RulebookProfile): CharacterCardVariant =>
  getRulebookVariant({
    visibility: profile.visibility,
    status: profile.status,
    moderation_status: profile.moderationStatus,
    moderation_report_status: profile.moderationReportStatus,
  });

type RulebookProfileBodyProps = {
  profile: RulebookProfile;
  isCoverBlurred: boolean;
  hasStatusChip: boolean;
  variant: CharacterCardVariant;
  tags: string[];
  statsValues: RulebookProfileStatsValues;
  actions: ReactNode;
};

export const RulebookProfileBody = ({ profile, isCoverBlurred, hasStatusChip, variant, tags, statsValues, actions }: RulebookProfileBodyProps) => {
  const { t } = useTranslation();

  return (
    <Row className="flex-col items-start gap-5 md:flex-row" data-testid="RulebookProfile-Body">
      <AvatarWithModal src={profile.coverUrl ?? ""} blurred={isCoverBlurred}>
        <Image
          src={getAvatarUrl(profile.coverUrl ?? "", { preset: AvatarPreset.IMAGE_640X832 }) || undefined}
          alt={t("rulebook:profile.coverAlt")}
          data-testid="RulebookProfileHeader-Cover"
          className={`cursor-pointer w-[156px] md:w-[200px] aspect-[640/832] rounded-[14px] object-cover ${isCoverBlurred ? "blur sm:blur-lg" : "blur-none"}`}
        />
      </AvatarWithModal>
      <Col className="min-w-0 flex-1 gap-3">
        <Col className="gap-1">
          <Text as="h1" variant={TextVariants.HEADING_4_BOLD} className="text-[28px] leading-tight md:text-[32px]">
            {profile.name}
          </Text>
          <Row className="flex-wrap items-center gap-2">
            {profile.creatorUsername ? (
              <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-gray-11">
                <Trans i18nKey="rulebook:profile.by" />
              </Text>
            ) : null}
            {profile.creatorUsername ? (
              <LocalizedLink
                className="text-link"
                to={`/creator/${profile.creatorUsername}`}
                aria-label="creator-profile"
                data-testid="RulebookProfileHeader-CreatorLink"
              >
                <Text variant={TextVariants.LABEL_MEDIUM_REGULAR}>@{profile.creatorUsername}</Text>
              </LocalizedLink>
            ) : null}
            {profile.definitionVisible ? <DefinitionSharedPill /> : <DefinitionPrivatePill />}
            {hasStatusChip ? <RulebookStatusChip variant={variant} /> : null}
          </Row>
        </Col>
        {profile.description ? (
          <Text variant={TextVariants.LABEL_LARGE_REGULAR} className="max-w-[680px] text-gray-11">
            {profile.description}
          </Text>
        ) : null}
        <RulebookProfileStats statsValues={statsValues} />
        {tags.length ? (
          <Row className="flex-wrap gap-1.5">
            {tags.map((tag) => (
              <Chip key={tag}>{tag}</Chip>
            ))}
          </Row>
        ) : null}
        {actions}
        {!profile.isOwner && !profile.definitionVisible ? (
          <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11" data-testid="RulebookProfile-PrivateActionsNote">
            <Trans i18nKey="rulebook:profile.privateActionsNote" />
          </Text>
        ) : null}
      </Col>
    </Row>
  );
};

/** Modal visibility + button handlers for the header action row. */
const useHeaderHandlers = ({ profile }: { profile: RulebookProfile }) => {
  const { t } = useTranslation();
  const { displayToast } = useToast();
  const navigate = useNavigate();
  const { clone, isCloning } = useCloneRulebook();
  const [stateIsShareVisible, setStateIsShareVisible] = useState(false);
  const [stateIsBlockVisible, setStateIsBlockVisible] = useState(false);
  const [stateIsOpening, setStateIsOpening] = useState(false);

  const handleShowShare = useCallback(() => {
    setStateIsShareVisible(true);
  }, []);

  const handleClone = useCallback(() => {
    clone(profile.id);
  }, [clone, profile.id]);

  const handleExport = useCallback(() => {
    if (profile.definition) {
      downloadRulebookExport({ definition: profile.definition });
    }
  }, [profile]);

  const handleReport = useCallback(() => {
    navigate(`${ROUTES.reportRulebook}/${profile.id}`);
  }, [navigate, profile.id]);

  const handleEdit = useCallback(() => {
    if (stateIsOpening) {
      return;
    }
    setStateIsOpening(true);
    openDraftOrImport({ profile, navigate, displayToast, t, setStateIsOpening });
  }, [stateIsOpening, profile, displayToast, navigate, t]);

  const handleShowBlock = useCallback(() => {
    setStateIsBlockVisible(true);
  }, []);

  const handleCloseBlock = useCallback(() => {
    setStateIsBlockVisible(false);
  }, []);

  return {
    isCloning,
    stateIsShareVisible,
    stateIsBlockVisible,
    stateIsOpening,
    setStateIsShareVisible,
    handleShowShare,
    handleClone,
    handleExport,
    handleReport,
    handleEdit,
    handleShowBlock,
    handleCloseBlock,
    t,
  };
};

export default function RulebookProfileHeader({ profile, isDraftOwnerReady, onRulebookAttached }: RulebookProfileHeaderProps) {
  const { blurNSFW } = useUserSettings();
  const { isAuthenticated } = useUserInfo();
  const { getTranslatedTag } = useTranslatedTag();
  const [isManageAllowed, isPublicSharingAllowed] = useApplicationCapabilitiesAllowed([
    SubCapabilityKey.RulebookManage,
    SubCapabilityKey.RulebookPublicSharing,
  ]);
  const { canAttach, isAttachVisible, handleShowAttach, handleCloseAttach } = useRulebookProfileAttach();
  const handlers = useHeaderHandlers({ profile });

  const shareUrl = `${window.location.origin}/rulebook/${profile.id}`;
  const isCoverBlurred = (!isAuthenticated || blurNSFW) && !!profile.isCoverNsfw;
  const isShareable = isRulebookShareable({ profile, isPublicSharingAllowed });
  // Any non-PUBLIC variant is state the owner should see: private, moderation, needs-review.
  const variant = rulebookProfileVariant(profile);
  const hasStatusChip = profile.isOwner && variant !== CharacterCardVariant.PUBLIC;
  const tags = useMemo(() => profile.tags.map((tag) => getTranslatedTag(tag)), [profile, getTranslatedTag]);
  const statsValues = useMemo<RulebookProfileStatsValues>(
    () => ({
      meters: profile.numMeters - profile.numHiddenMeters,
      hiddenMeters: profile.numHiddenMeters,
      rules: profile.numRules,
      characters: profile.numAttachedCharacters,
      personas: profile.numAttachedPersonas,
    }),
    [profile],
  );

  return (
    <div className="flex flex-col gap-6" data-testid="RulebookProfileHeader">
      <LocalizedLink
        to={ROUTES.rulebooksExplore}
        className="inline-flex self-start items-center gap-1 text-gray-11 hover:text-gray-12"
        data-testid="RulebookProfile-BackLink"
      >
        <Icon icon={IconVariants.CHEVRON_LEFT} size={IconSize.SMALL} />
        <Text variant={TextVariants.LABEL_MEDIUM_REGULAR}>
          <Trans i18nKey="rulebook:profile.backToRulebooks" />
        </Text>
      </LocalizedLink>
      <RulebookProfileBody
        profile={profile}
        isCoverBlurred={isCoverBlurred}
        hasStatusChip={hasStatusChip}
        variant={variant}
        tags={tags}
        statsValues={statsValues}
        actions={
          <RulebookProfileActions
            profile={profile}
            isShareable={isShareable}
            isCloning={handlers.isCloning}
            isOpening={handlers.stateIsOpening}
            canAttach={canAttach}
            onAttach={handleShowAttach}
            isDraftOwnerReady={isDraftOwnerReady}
            isManageAllowed={isManageAllowed}
            onShare={handlers.handleShowShare}
            onClone={handlers.handleClone}
            onExport={handlers.handleExport}
            onEdit={handlers.handleEdit}
            onReport={handlers.handleReport}
            onBlock={handlers.handleShowBlock}
            t={handlers.t}
          />
        }
      />
      <RulebookProfileModals
        profile={profile}
        isShareable={isShareable}
        isShareVisible={handlers.stateIsShareVisible}
        isBlockVisible={handlers.stateIsBlockVisible}
        isAttachVisible={isAttachVisible}
        shareUrl={shareUrl}
        setShowShare={handlers.setStateIsShareVisible}
        onCloseBlock={handlers.handleCloseBlock}
        onCloseAttach={handleCloseAttach}
        onRulebookAttached={onRulebookAttached}
      />
    </div>
  );
}
